/* eslint-disable max-lines */
import { toJS } from 'mobx';

import { getErrorString, Logger, NOOP_LOGGER, SystemEvent } from 'bernie-logger';
import { merge, SerializedData, timeoutPromise } from 'bernie-core';

import { SOURCE_EVENTS } from './events/source-events';

import {
  AddPropertyToCart,
  Cart,
  CreateMultiItemSession,
  CreateSessionCart,
  EmptyCart,
  InitiateMultiItemSession,
  LodgingPwaPropertySearch,
  LodgingPwaPropertySearchSortAndFilter,
  OperationResult,
  PrepareCheckout,
  PrepareCheckoutByCartId,
  PropertyInfoPageLayout,
  PropertyLocationScoreInfo,
  PropertyRateCalendar,
  PropertyRoomScoreInfo,
  PropertySearchTravelAds,
  RemoveItemFromCart,
  Sdk,
  SearchResultsPageLayout,
  SelectProducts,
} from '__generated__/api';

const { withoutKeys } = merge;

export class BexApiSource {
  private readonly api: Sdk;
  private readonly logger: Logger;

  constructor(api: Sdk, logger: Logger = NOOP_LOGGER) {
    this.api = api;
    this.logger = logger;
  }

  public fetchPageLayoutTemplate(variables: PropertyInfoPageLayout.Variables) {
    return this.api
      .PropertyInfoPageLayout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_INFO_PAGE_LAYOUT_FETCH_ERROR));
  }

  public fetchSRPLayoutTemplate(variables: SearchResultsPageLayout.Variables) {
    return this.api
      .SearchResultsPageLayout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_SEARCH_RESULTS_PAGE_LAYOUT_FETCH_ERROR));
  }

  public fetchSortAndFilter(
    variables: LodgingPwaPropertySearchSortAndFilter.Variables,
    pageId: string
  ): Promise<OperationResult<LodgingPwaPropertySearchSortAndFilter.Query>> {
    return this.api
      .LodgingPwaPropertySearchSortAndFilter(variables, { 'x-page-id': pageId })
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_SEARCH_SORT_AND_FILTER_FETCH_ERROR));
  }

  public searchProperties(
    variables: LodgingPwaPropertySearch.Variables,
    pageId: string
  ): Promise<OperationResult<LodgingPwaPropertySearch.Query>> {
    return this.api
      .LodgingPwaPropertySearch(variables, { 'x-page-id': pageId })
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_FULL_PROPERTY_SEARCH_FETCH_ERROR));
  }

  public fetchTravelAdPropertySearch(
    variables: LodgingPwaPropertySearch.Variables,
    pageId: string
  ): Promise<OperationResult<PropertySearchTravelAds.Query>> {
    return this.api
      .PropertySearchTravelAds(variables, { 'x-page-id': pageId })
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_TRAVELAD_SEARCH_FETCH_ERROR));
  }

  public fetchPropertyRoomScoreInfo(
    variables: PropertyRoomScoreInfo.Variables,
    pageId: string,
    timeout: number
  ): Promise<OperationResult<PropertyRoomScoreInfo.Query>> {
    return timeoutPromise<OperationResult<PropertyLocationScoreInfo.Query>>(
      this.api.PropertyRoomScoreInfo(variables, { 'x-page-id': pageId }),
      timeout,
      'PropertyRoomScoreInfo timeout error'
    ).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_ROOM_SCORE_FETCH_ERROR));
  }

  public fetchPropertyLocationScoreInfo(
    variables: PropertyLocationScoreInfo.Variables,
    pageId: string,
    timeout: number
  ): Promise<OperationResult<PropertyLocationScoreInfo.Query>> {
    return timeoutPromise(
      this.api.PropertyLocationScoreInfo(variables, { 'x-page-id': pageId }),
      timeout,
      'PropertyLocationScoreInfo timeout error'
    ).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_LOCATION_SCORE_FETCH_ERROR));
  }

  public createSessionCart(variables: CreateSessionCart.Variables): Promise<OperationResult<CreateSessionCart.Mutation>> {
    return this.api.CreateSessionCart(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_CART_ERROR));
  }

  public addPropertyToCart(variables: AddPropertyToCart.Variables): Promise<OperationResult<AddPropertyToCart.Mutation>> {
    return this.api
      .AddPropertyToCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_ADD_ITEM_TO_CART_ERROR));
  }

  public createMultiItemSession(variables: CreateMultiItemSession.Variables): Promise<OperationResult<CreateMultiItemSession.Mutation>> {
    return this.api
      .CreateMultiItemSession(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_MULTIITEM_SESSION));
  }

  public selectProducts(variables: SelectProducts.Variables): Promise<OperationResult<SelectProducts.Mutation>> {
    return this.api.SelectProducts(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_SELECT_PRODUCTS));
  }

  public initiateMultiItemSession(
    variables: InitiateMultiItemSession.Variables
  ): Promise<OperationResult<InitiateMultiItemSession.Mutation>> {
    return this.api
      .InitiateMultiItemSession(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_INITIATE_MULTIITEM_SESSION));
  }

  public fetchCart(variables: Cart.Variables): Promise<OperationResult<Cart.Query>> {
    return this.api.Cart(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CART_FETCH_ERROR));
  }

  public removeItemFromCart(variables: RemoveItemFromCart.Variables): Promise<OperationResult<RemoveItemFromCart.Mutation>> {
    return this.api
      .RemoveItemFromCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public emptyCart(variables: EmptyCart.Variables): Promise<OperationResult<EmptyCart.Mutation>> {
    return this.api
      .EmptyCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public prepareCheckout(variables: PrepareCheckout.Variables): Promise<OperationResult<PrepareCheckout.Mutation>> {
    return this.api
      .PrepareCheckout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_ERROR));
  }

  public prepareCheckoutByCartId(variables: PrepareCheckoutByCartId.Variables): Promise<OperationResult<PrepareCheckoutByCartId.Mutation>> {
    return this.api
      .PrepareCheckoutByCartId(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_BY_CART_ID_ERROR));
  }

  public fetchRateCalendar(
    variables: PropertyRateCalendar.Variables,
    pageId: string
  ): Promise<OperationResult<PropertyRateCalendar.Query>> {
    return this.api
      .PropertyRateCalendar(variables, { 'x-page-id': pageId })
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_RATE_CALENDAR_FETCH_ERROR));
  }

  private getErrorHandler(variables: any, sourceEvent: SystemEvent) {
    return (error: any) => {
      this.logger.logEvent(sourceEvent, variables, getErrorString(error));
      // Throw the error to the next catch block since it is not resolved here
      throw error;
    };
  }

  /**
   * Override the default `toJson` to exclude the Apollo client.
   * If we don't include this, we will get a JSON serialization error.
   *
   * Creates a basic JSON object from the store, excluding
   * any fields provided in `ignoreAdditionalKeys`.
   */
  public toJSON(ignoreAdditionalKeys: string[] | string = []): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }

    const regexIgnorePrivate = /^_.+/;
    const simpleObject: any = toJS(this);
    const ignoreKeys: string[] = Object.keys(simpleObject)
      .filter(key => regexIgnorePrivate.test(key) || typeof simpleObject[key] === 'function')
      .concat(ignoreAdditionalKeys, 'logger', 'api', 'headers', 'apiWithFederation');

    return withoutKeys(ignoreKeys).merge({}, simpleObject);
  }
}
